// OWL Mobile shell — consultative mode. NEVER import api.create*/update*/delete*/patch*/regenerate*
// from this tree. Reads only. Mutations live on the desktop shell.

import type { ApplicationEnvironment, User } from '@owl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { hashToRoute } from '../../lib/route';
import { connectLogStream } from '../../lib/sse';
import { applyTheme, readSettingsTheme } from '../../lib/theme';
import '../../styles/mobile.css';

import { Icon } from '../icons';
import { BottomSheetHost } from './BottomSheet';
import { InstallPrompt } from './InstallPrompt';
import type {
  MobileRange,
  MobileRoute,
  MobileSheet,
  MobileShellCtx,
  MobileViewMode,
} from './MobileShellCtx';
import { MobileTabBar } from './MobileTabBar';
import { MobileTopBar } from './MobileTopBar';
import { AlertsPage } from './pages/AlertsPage';
import { AppDashboardPage } from './pages/AppDashboardPage';
import { AppsPage } from './pages/AppsPage';
import { OverviewPage } from './pages/OverviewPage';
import { SettingsPage } from './pages/SettingsPage';
import { usePullToRefresh } from './usePullToRefresh';

const ENV_STORAGE_KEY = 'owl.env';
const VIEWMODE_STORAGE_KEY = 'owl.m.viewMode';
const UNREAD_STORAGE_KEY = 'owl.m.alertsLastSeen';

function loadEnv(): ApplicationEnvironment {
  if (typeof window === 'undefined') return 'production';
  const v = window.localStorage.getItem(ENV_STORAGE_KEY);
  return v === 'development' ? 'development' : 'production';
}

function loadViewMode(): MobileViewMode {
  if (typeof window === 'undefined') return 'logs';
  const v = window.localStorage.getItem(VIEWMODE_STORAGE_KEY);
  return v === 'ai' ? 'ai' : 'logs';
}

export function MobileShell({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [route, setRoute] = useState<MobileRoute>({ name: 'overview' });
  const [, setHistoryStack] = useState<MobileRoute[]>([]);
  const [range, setRange] = useState<MobileRange>('24h');
  const [environment, setEnvironmentState] = useState<ApplicationEnvironment>(loadEnv);
  const [viewMode, setViewModeState] = useState<MobileViewMode>(loadViewMode);
  const [refreshTick, setRefreshTick] = useState(0);
  const [sheet, setSheet] = useState<MobileSheet | null>(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [liveConnected, setLiveConnected] = useState(false);

  // Marks <html> for the duration of the mobile shell so the viewport lock in
  // mobile.css applies only here — the desktop shell shares that stylesheet.
  useEffect(() => {
    document.documentElement.classList.add('m-shell');
    return () => document.documentElement.classList.remove('m-shell');
  }, []);

  const lastSeenRef = useRef<number>(0);
  useEffect(() => {
    const raw = window.localStorage.getItem(UNREAD_STORAGE_KEY);
    lastSeenRef.current = raw ? Number(raw) : Date.now();
  }, []);

  // theme bootstrap (same as desktop Shell)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getSettings();
        if (cancelled) return;
        applyTheme(readSettingsTheme(r.settings));
      } catch {
        // keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // auto refresh (every 30s, mobile is read-only and lightweight)
  useEffect(() => {
    const id = window.setInterval(() => setRefreshTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // SSE — live indicator + unread alerts counter
  useEffect(() => {
    setLiveConnected(false);
    const close = connectLogStream(
      {},
      {
        onLog: () => {
          setLiveConnected(true);
        },
        onAlert: () => {
          setLiveConnected(true);
          setUnreadAlerts((n) => n + 1);
        },
        onError: () => setLiveConnected(false),
      },
    );
    const t = window.setTimeout(() => setLiveConnected(true), 1500);
    return () => {
      close();
      window.clearTimeout(t);
    };
  }, []);

  const setEnvironment = useCallback((env: ApplicationEnvironment) => {
    setEnvironmentState(env);
    try {
      window.localStorage.setItem(ENV_STORAGE_KEY, env);
    } catch {
      // ignore
    }
  }, []);

  const setViewMode = useCallback((m: MobileViewMode) => {
    setViewModeState(m);
    try {
      window.localStorage.setItem(VIEWMODE_STORAGE_KEY, m);
    } catch {
      // ignore
    }
  }, []);

  const navigate = useCallback((next: MobileRoute) => {
    setRoute((prev) => {
      if (prev.name !== next.name || JSON.stringify(prev) !== JSON.stringify(next)) {
        setHistoryStack((s) => [...s, prev].slice(-12));
      }
      return next;
    });
  }, []);

  const goBack = useCallback(() => {
    setHistoryStack((s) => {
      const last = s[s.length - 1];
      if (!last) {
        setRoute({ name: 'overview' });
        return s;
      }
      setRoute(last);
      return s.slice(0, -1);
    });
  }, []);

  // Deep links arrive as a URL hash, in the same format the desktop shell uses: a
  // notification tap either cold-starts the PWA with it, or — with OWL already open —
  // main.tsx sets it from a service worker message. This shell does not keep its route
  // in the URL, so the hash is consumed and then cleared. Clearing is also what lets the
  // next notification for the same target fire hashchange again.
  useEffect(() => {
    const consume = () => {
      const hash = window.location.hash;
      if (!hash || hash === '#') return;
      const target = hashToRoute(hash);
      if (target.name === 'appdash') {
        setSheet(null);
        navigate({
          name: 'appdash',
          applicationId: target.applicationId,
          tab: 'logs',
          focusLogId: target.logId,
        });
      } else if (target.name === 'alerts') {
        setSheet(null);
        navigate({ name: 'alerts' });
      }
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    };
    consume();
    window.addEventListener('hashchange', consume);
    return () => window.removeEventListener('hashchange', consume);
  }, [navigate]);

  const triggerRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const scrollRef = useRef<HTMLElement>(null);
  const { pull, refreshing } = usePullToRefresh(scrollRef, triggerRefresh, route.name);

  const openSheet = useCallback((s: MobileSheet) => setSheet(s), []);
  const closeSheet = useCallback(() => setSheet(null), []);

  const resetUnreadAlerts = useCallback(() => {
    setUnreadAlerts(0);
    lastSeenRef.current = Date.now();
    try {
      window.localStorage.setItem(UNREAD_STORAGE_KEY, String(lastSeenRef.current));
    } catch {
      // ignore
    }
  }, []);

  const ctx = useMemo<MobileShellCtx>(
    () => ({
      user,
      onLogout,
      route,
      navigate,
      goBack,
      range,
      setRange,
      environment,
      setEnvironment,
      viewMode,
      setViewMode,
      refreshTick,
      triggerRefresh,
      sheet,
      openSheet,
      closeSheet,
      unreadAlerts,
      resetUnreadAlerts,
      liveConnected,
    }),
    [
      user,
      onLogout,
      route,
      navigate,
      goBack,
      range,
      environment,
      setEnvironment,
      viewMode,
      setViewMode,
      refreshTick,
      triggerRefresh,
      sheet,
      openSheet,
      closeSheet,
      unreadAlerts,
      resetUnreadAlerts,
      liveConnected,
    ],
  );

  return (
    <div className={`m-app m-env-${environment}`}>
      <MobileTopBar ctx={ctx} />
      <main className="m-scroll" key={route.name} ref={scrollRef}>
        <div
          className={`ptr ${pull > 0 || refreshing ? 'active' : ''}`}
          style={refreshing ? undefined : { height: pull }}
        >
          <Icon.Refresh size={13} />
          {refreshing
            ? 'Actualizando…'
            : pull >= 64
              ? 'Suelta para actualizar'
              : 'Desliza para actualizar'}
        </div>
        {route.name === 'overview' && <OverviewPage ctx={ctx} />}
        {route.name === 'apps' && <AppsPage ctx={ctx} />}
        {route.name === 'appdash' && (
          <AppDashboardPage
            ctx={ctx}
            applicationId={route.applicationId}
            tab={route.tab ?? 'logs'}
            focusLogId={route.focusLogId}
          />
        )}
        {route.name === 'alerts' && <AlertsPage ctx={ctx} tab={route.tab ?? 'events'} />}
        {route.name === 'settings' && <SettingsPage ctx={ctx} />}
      </main>
      <MobileTabBar ctx={ctx} />
      <InstallPrompt />
      <BottomSheetHost ctx={ctx} />
    </div>
  );
}
